"use client";

import { useController } from "react-hook-form";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { useI18n } from "@/locales/client";
import { InputError } from "./input-error";
import { ReactElement, ReactNode, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Close } from "@mui/icons-material";
import { KIcon } from "@/components/k-icon";
import { z } from "zod";
import { ZodControlledField } from "@/common/use-k-form";

type KSelectProps<TFieldValues extends z.Schema> = {
    children: ReactElement<KOptionProps>[];
    label?: string;
    placeholder?: string;
    allowClear?: boolean;
    isDisabled?: boolean;
    className?: string;
    prefix?: ReactNode;
} & ZodControlledField<TFieldValues, string | undefined>;

export function KSelect<TFieldValues extends z.Schema>(
    props: KSelectProps<TFieldValues>,
) {
    const t = useI18n();
    const { field, fieldState } = useController({
        name: props.name,
        control: props.control,
    });

    useEffect(() => {
        if (field.value === undefined || field.value === null) {
            field.onChange("");
        }
    }, [field]);

    return (
        <div className={`flex flex-col gap-2 ${props.className ?? ""}`}>
            {!!props.label && (
                <Label
                    htmlFor={field.name}
                    className={fieldState.error && "text-destructive"}
                >
                    {props.label}
                </Label>
            )}
            <div className="relative flex gap-2">
                <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={field.disabled || props.isDisabled}
                >
                    <SelectTrigger
                        className={cn(
                            "rounded-sm border-neutral-300 font-extralight",
                            props.prefix && "pl-0",
                        )}
                    >
                        <div className="flex items-center">
                            {props.prefix && (
                                <span className="flex h-10 w-10 items-center justify-center">
                                    {props.prefix}
                                </span>
                            )}
                            <SelectValue
                                placeholder={
                                    props.placeholder ?? t("SelectAnOption")
                                }
                            />
                        </div>
                    </SelectTrigger>
                    <SelectContent>{props.children}</SelectContent>
                </Select>
                {props.allowClear && (
                    <Button
                        className="shrink-0"
                        variant="link-secondary"
                        type="button"
                        size="icon"
                        onClick={() => field.onChange("")}
                    >
                        <KIcon icon={<Close />} />
                    </Button>
                )}
                {!!fieldState.error?.message && (
                    <InputError errorMessage={fieldState.error.message} />
                )}
            </div>
        </div>
    );
}

export interface KOptionProps {
    value: string;
    children: ReactNode;
}

export function KOption(props: KOptionProps) {
    return <SelectItem value={props.value}>{props.children}</SelectItem>;
}
